<template>
    <div class="flex flex-col xl:flex-row gap-4 xl:gap-10 text-xs text-grey-90"
         v-if="footerDateValue || showOrgInfoName || ifMarketFooter">

        <div v-if="footerDateValue" class="flex items-center gap-1 shrink-0">
            <span>{{ footerDateLabel }}:</span>
            {{ footerDateValue }}
        </div>

        <!-- Owner -->
        <div class="flex items-center gap-1" v-if="showOrgInfoName">
            <span class="shrink-0">{{ t('solutionCard.owner') }}:</span>

            <SolutionOrgIcon :org-info-logo="OrgInfo?.logo" class="h-4 w-4 shrink-0" />

            <div class="grow flex items-center gap-2">
                <a v-if="companyHref" :href="companyHref" target="blank" class="hover:underline line-clamp-1" :title="showOrgInfoName" v-html="showOrgInfoName"></a>
                <span v-else :title="showOrgInfoName" v-html="showOrgInfoName" />
                <OrgPopover v-if="showOrgInfoCounts" :counts="showOrgInfoCounts" :orgs="profile.ORG_INFO"></OrgPopover>
            </div>
        </div>

        <div v-if="ifMarketFooter" class="flex text-xs flex-wrap">
            <div v-if="cagr" class="grow flex items-center gap-1 mr-10 mb-2">
                <span class="shrink-0">{{ t('solutionCard.cagr') }}:</span>
                <span>{{ cagr }}</span>
            </div>
            <div v-if="category" class="grow flex items-center gap-1 mr-10 mb-2">
                <span class="shrink-0">{{ t('solutionCard.category') }}:</span>
                <a :href="categoryLink"
                   class="cursor-pointer text-eureka-500"
                   target="_blank"
                   v-html="category.name"
                />
            </div>
            <div v-if="publisher || publishedTime" class="grow flex items-center gap-2">
                <span v-if="publishedTime">{{ publishedTime }}</span>
                <span v-if="publishedTime && publisher">/</span>
                <span v-if="publisher">{{ publisher }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ISolution } from '@eureka/solution-card/src/types';
import { SOLUTION_TYPE } from '@eureka/solution-card/src/types';
import { generateCompanyHref } from '@eureka/utils/company';
import { Site, SLD } from '@patsnap-utils/biz';
import _ from 'lodash';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { cn, en, jp, tw } from './locales';
import OrgPopover from './OrgPopover.vue';
import SolutionOrgIcon from './SolutionOrgIcon.vue';

const { t } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
})

const props = defineProps<{
    profile: ISolution
    footerField?: string
}>()

const currentDateField = computed(() => {
    if (props.footerField) {
        return props.footerField
    }
    if (props.profile.SOLUTION_TYPE === SOLUTION_TYPE.PATENT) {
        return _.get(props.profile, 'PBD') ? 'PBD' : 'APD'
    }
    return 'PBD'
})

const footerDateLabel = computed(() => {
    return t(`solutionCard.${props.profile.SOLUTION_TYPE}.${currentDateField.value}`)
})

const footerDateValue = computed(() => {
    return _.get(props.profile, currentDateField.value) || _.get(props.profile, 'publication_time')
})

const companyHref = computed(() => {
    const orgId = _.get(props.profile, 'ORG_INFO[0].id', '')
    return generateCompanyHref(orgId)
})

const showOrgInfoName = computed(() => {
    return _.get(props.profile, 'ORG_INFO[0].display_name', '')
})

const OrgInfo = computed(() => {
    return _.get(props.profile, 'ORG_INFO[0]')
})

const showOrgInfoCounts = computed(() => {
    return _.get(props.profile, 'ORG_INFO', []).length ? _.get(props.profile, 'ORG_INFO', []).length - 1 : 0
})

const cagr = computed(() => {
    const cagrAvg = _.get(props.profile, 'AVG_CAGR')
    return cagrAvg ? `${cagrAvg * 100}%` : ''
})

const category = computed(() => {
    return _.get(props.profile, 'CATEGORY[0]')
})

const publishedTime = computed(() => {
    return _.get(props.profile, 'PUBLISHED_TIME')
})

const publisher = computed(() => {
    return _.get(props.profile, 'PUBLISHER')
})

const ifMarketFooter = computed(() => {
    return cagr.value || category.value || publisher.value || publishedTime.value
})

const categoryLink = computed(() => {
    return `${new Site({ sld: SLD.EUREKA }).href}/discovery/#/market-category-detail/${props.profile.CATEGORY?.[0]?.id}`
})

</script>
<style lang="scss">
.org_logo {
    width: 16px !important;
    height: 16px !important;
    border-radius: 4px;
    border: 1px solid var(--border-light);
    margin-right: 4px;
    vertical-align: text-bottom !important;
    object-fit: contain;
}
</style>
